import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Box, Snackbar } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FolderTabs from '../../components/common/FolderTabs';
import PrimaryActionButton from '../../components/common/PrimaryActionButton';
import ManagementPageLayout, { ManagementPageCard } from '../../components/layout/ManagementPageLayout';
import ManagementPageSkeleton from '../../components/layout/ManagementPageSkeleton';
import TeamMemberPersonalInfo from '../../components/team-member-registration/TeamMemberPersonalInfo';
import TeamMemberTimeOffDialog from '../../components/team-members/TeamMemberTimeOffDialog';
import StoreCreationBlockedAlert from '../../components/stores/StoreCreationBlockedAlert';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import usePermissions from '../../hooks/usePermissions';
import useStorePlan from '../../hooks/useStorePlan';
import { DEFAULT_LANGUAGE, normalizeLanguage } from '../../constants/Languages';
import TeamMemberService from '../../services/teamMemberService';
import requestDeduplicator from '../../utils/requestDeduplicator';

const PROFILE_TAB = 'profile';
const TIME_OFF_TAB = 'time-off';

const OwnerProfilePage = () => {
  const { t } = useTranslation();
  const { storeId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { managementUser } = useAuth();
  const { showError, showSuccess } = useToast();
  const { isOwner } = usePermissions(storeId);
  const { isFreeOrLapsed } = useStorePlan(storeId, Boolean(storeId));
  const teamMemberId = managementUser?.id || managementUser?.userId;

  const [teamMember, setTeamMember] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phoneCountryCode: '+351',
    phoneNumber: '',
    jobTitle: '',
    genre: '',
    birthDate: null,
    language: DEFAULT_LANGUAGE,
    roleId: null,
    additionalPermissions: [],
    services: [],
    acceptsCustomerBookings: true,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const activeTab = searchParams.get('tab') === TIME_OFF_TAB ? TIME_OFF_TAB : PROFILE_TAB;

  const tabs = [
    { value: PROFILE_TAB, label: t('ownerProfile.tabs.profile') },
    { value: TIME_OFF_TAB, label: t('ownerProfile.tabs.timeOff') },
  ];

  const loadOwner = useCallback(async () => {
    if (!storeId || !teamMemberId) {
      setLoading(false);
      return;
    }

    return requestDeduplicator.deduplicate(`owner-profile-${storeId}-${teamMemberId}`, async () => {
      try {
        setLoading(true);
        const teamMemberData = await TeamMemberService.getTeamMember(teamMemberId, storeId);
        setTeamMember(teamMemberData);

        let birthDate = null;
        if (teamMemberData.birthDate) {
          birthDate = new Date(teamMemberData.birthDate);
        }

        setFormData({
          name: teamMemberData.name || '',
          email: teamMemberData.email || '',
          phoneCountryCode: teamMemberData.phone?.countryCode || '+351',
          phoneNumber: teamMemberData.phone?.number || '',
          jobTitle: teamMemberData.store?.jobTitle || '',
          genre: teamMemberData.genre?.toLowerCase() || '',
          birthDate,
          language: normalizeLanguage(teamMemberData.language) || DEFAULT_LANGUAGE,
          roleId: teamMemberData.store?.roleId || null,
          additionalPermissions: teamMemberData.store?.additionalPermissions || [],
          services: (teamMemberData.services || []).map((service) => ({
            serviceId: service.serviceId,
            customPrice: service.customPrice || null,
          })),
          acceptsCustomerBookings: true,
        });
      } catch (error) {
        console.error('Failed to load owner profile:', error);
        showError(t('ownerProfile.error.load'));
      } finally {
        setLoading(false);
      }
    });
  }, [showError, storeId, t, teamMemberId]);

  useEffect(() => {
    loadOwner();
  }, [loadOwner]);

  const updateFormData = (updates) => {
    setFormData((prev) => ({ ...prev, ...updates }));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(updates).forEach((key) => {
        if (updates[key] && updates[key].toString().trim()) {
          delete next[key];
        }
      });
      return next;
    });
  };

  const handleSave = async () => {
    const nextErrors = {};
    if (!formData.name.trim()) {
      nextErrors.name = t('teamMembers.registration.validation.nameRequired');
    }
    if (!formData.phoneNumber.trim()) {
      nextErrors.phoneNumber = t('teamMembers.registration.validation.phoneRequired');
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    let birthDateForBackend = null;
    if (formData.birthDate) {
      const year = formData.birthDate.getFullYear();
      const month = String(formData.birthDate.getMonth() + 1).padStart(2, '0');
      const day = String(formData.birthDate.getDate()).padStart(2, '0');
      birthDateForBackend = `${year}-${month}-${day}`;
    }

    try {
      setSaving(true);
      await TeamMemberService.updateTeamMember(teamMemberId, {
        name: formData.name,
        phone: {
          countryCode: formData.phoneCountryCode,
          number: formData.phoneNumber,
        },
        genre: formData.genre || null,
        ...(birthDateForBackend && { birthDate: birthDateForBackend }),
        services: formData.services || [],
        language: formData.language.toUpperCase(),
        storeId,
        roleId: formData.roleId || null,
        jobTitle: formData.jobTitle?.trim() || null,
        additionalPermissions: formData.additionalPermissions || [],
        acceptsCustomerBookings: true,
      });
      showSuccess(t('ownerProfile.saved'));
      setSnackbar({ open: true, message: t('ownerProfile.saved'), severity: 'success' });
    } catch (error) {
      console.error('Failed to save owner profile:', error);
      showError(t('ownerProfile.error.save'));
    } finally {
      setSaving(false);
    }
  };

  if (!storeId) {
    return <Navigate to="/management/stores" replace />;
  }

  if (loading) {
    return (
      <ManagementPageLayout
        pageTitle={t('ownerProfile.title')}
        pageSubtitle={t('ownerProfile.subtitle')}
      >
        <ManagementPageSkeleton variant="form" />
      </ManagementPageLayout>
    );
  }

  return (
    <ManagementPageLayout
      id="owner-profile-page"
      pageTitle={t('ownerProfile.title')}
      pageSubtitle={t('ownerProfile.subtitle')}
    >
      <FolderTabs
        tabs={tabs}
        value={activeTab}
        onChange={(nextTab) => setSearchParams(nextTab === PROFILE_TAB ? {} : { tab: nextTab }, { replace: true })}
        contentId="owner-profile-tabs-content"
        equalWidthOnMobile
      >
        <Box role="tabpanel" hidden={activeTab !== PROFILE_TAB} sx={{ minWidth: 0 }}>
          {activeTab === PROFILE_TAB ? (
            <ManagementPageCard>
              <Box sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
                {isOwner && isFreeOrLapsed ? (
                  <StoreCreationBlockedAlert
                    billingSetupPath={`/management/stores/${storeId}/payment-settings`}
                    titleKey="storePlan.extraMemberTitle"
                    messageKey="storePlan.extraMemberMessage"
                    actionKey="storePlan.upgradeCta"
                    sx={{ mb: 3 }}
                  />
                ) : null}
                <TeamMemberPersonalInfo
                  formData={formData}
                  updateFormData={updateFormData}
                  errors={errors}
                  mode="update"
                  hideAcceptsBookings
                  hideHeading
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                  <PrimaryActionButton onClick={handleSave} disabled={saving}>
                    {saving ? t('common.saving') : t('common.save')}
                  </PrimaryActionButton>
                </Box>
              </Box>
            </ManagementPageCard>
          ) : null}
        </Box>
        <Box role="tabpanel" hidden={activeTab !== TIME_OFF_TAB} sx={{ minWidth: 0 }}>
          {activeTab === TIME_OFF_TAB ? (
            <ManagementPageCard>
              <Box sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
                <TeamMemberTimeOffDialog
                  open
                  embedded
                  teamMember={teamMember || { id: teamMemberId, name: formData.name }}
                  storeId={storeId}
                />
              </Box>
            </ManagementPageCard>
          ) : null}
        </Box>
      </FolderTabs>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ManagementPageLayout>
  );
};

export default OwnerProfilePage;
